import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function ShopSettings() {
  const [store, setStore] = useState(null);

  useEffect(() => { api.get("/stores/my").then((r) => setStore(r.data)); }, []);

  if (!store) return <div className="p-6">Loading…</div>;

  const updateSettings = async (changes) => {
    const newSettings = { ...store.settings, ...changes };
    setStore({ ...store, settings: newSettings });
    try {
      await api.patch("/stores/my", { settings: newSettings });
      toast.success("Saved");
    } catch { toast.error("Failed"); }
  };

  const updateStore = async (changes) => {
    setStore({ ...store, ...changes });
    try {
      await api.patch("/stores/my", changes);
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 max-w-3xl">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">Store settings</h1>
        <p className="text-gray-600 text-sm">Customize your store</p>
      </div>

      <div className="qd-card p-5 space-y-4">
        <h3 className="font-heading text-xl font-bold">Store info</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Name</label>
            <input className="qd-input mt-2" value={store.name} onChange={(e) => setStore({ ...store, name: e.target.value })}
              onBlur={(e) => updateStore({ name: e.target.value })} data-testid="store-name" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Phone</label>
            <input className="qd-input mt-2" value={store.phone} onChange={(e) => setStore({ ...store, phone: e.target.value })}
              onBlur={(e) => updateStore({ phone: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Description</label>
            <input className="qd-input mt-2" value={store.description} onChange={(e) => setStore({ ...store, description: e.target.value })}
              onBlur={(e) => updateStore({ description: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Banner image URL</label>
            <input className="qd-input mt-2" value={store.banner} onChange={(e) => setStore({ ...store, banner: e.target.value })}
              onBlur={(e) => updateStore({ banner: e.target.value })} />
          </div>
        </div>
      </div>

      <div className="qd-card p-5 space-y-4">
        <h3 className="font-heading text-xl font-bold">Delivery</h3>
        <Toggle label="Self delivery enabled" v={store.settings.self_delivery_enabled}
          onChange={(v) => updateSettings({ self_delivery_enabled: v })} testId="toggle-self" />
        <Toggle label="3rd party delivery (Borzo/Porter)" v={store.settings.third_party_delivery_enabled}
          onChange={(v) => updateSettings({ third_party_delivery_enabled: v })} testId="toggle-3p" />
        <Toggle label="COD enabled" v={store.settings.cod_enabled}
          onChange={(v) => updateSettings({ cod_enabled: v })} testId="toggle-cod" />
        <Toggle label="COD for self delivery" v={store.settings.cod_for_self_delivery}
          onChange={(v) => updateSettings({ cod_for_self_delivery: v })} testId="toggle-cod-self" />
        <Toggle label="Instant delivery" v={store.settings.instant_delivery_enabled}
          onChange={(v) => updateSettings({ instant_delivery_enabled: v })} testId="toggle-instant" />

        <div className="grid sm:grid-cols-3 gap-3 pt-3 border-t border-gray-100">
          <NumField label="Min order ₹" value={store.settings.min_order_value} onSave={(v) => updateSettings({ min_order_value: v })} />
          <NumField label="Self delivery charge ₹" value={store.settings.self_delivery_charge} onSave={(v) => updateSettings({ self_delivery_charge: v })} />
          <NumField label="Free delivery above ₹" value={store.settings.free_delivery_threshold} onSave={(v) => updateSettings({ free_delivery_threshold: v })} />
        </div>
      </div>
    </div>
  );
}

function Toggle({ label, v, onChange, testId }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-sm font-semibold">{label}</div>
      <button onClick={() => onChange(!v)} data-testid={testId}
        className={`w-12 h-7 rounded-full transition relative ${v ? "bg-[#15803D]" : "bg-gray-300"}`}>
        <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white transition-all ${v ? "left-[22px]" : "left-0.5"}`} />
      </button>
    </div>
  );
}

function NumField({ label, value, onSave }) {
  const [v, setV] = useState(value);
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-widest text-gray-500">{label}</label>
      <input type="number" className="qd-input mt-2" value={v} onChange={(e) => setV(e.target.value)}
        onBlur={() => onSave(parseFloat(v))} />
    </div>
  );
}
